import { Component, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { EmailEditorComponent } from 'angular-email-editor';
import { buildMergeTags } from '../utils/merge-tags';

export interface DesignerResult {
    html: string;
    design: object;
}

/**
 * The one wrapper around the Unlayer editor. Content is loaded only after (ready),
 * and read back only through getResult() when the user saves or previews.
 */
@Component({
    selector: 'app-email-designer',
    template: `
        <email-editor
            [options]="options"
            [minHeight]="minHeight"
            (ready)="onReady()">
        </email-editor>
    `,
    styles: [':host { display: flex; flex: 1; min-height: 0; }'],
})
export class EmailDesignerComponent implements OnInit, OnDestroy {

    /** Unlayer design to open; null starts a blank design. */
    @Input() design: object | null = null;
    /** Allowed {{variables}}, offered as merge tags. */
    @Input() variables: string[] = [];
    @Input() minHeight = '650px';
    /** Hosts an image picked in the editor and resolves to its public URL. */
    @Input() imageUpload: (file: File) => Promise<string>;

    @Output() dirty = new EventEmitter<void>();
    @Output() ready = new EventEmitter<void>();
    @Output() imageUploadFailed = new EventEmitter<any>();

    @ViewChild(EmailEditorComponent) private emailEditor: EmailEditorComponent;

    options: any;
    isReady = false;

    constructor(private _ngZone: NgZone) { }

    private get unlayer(): any {
        return this.emailEditor?.editor;
    }

    ngOnInit(): void {
        // Read once by <email-editor> when it creates the editor.
        this.options = {
            displayMode: 'email',
            mergeTags: buildMergeTags(this.variables),
            tools: {
                html: { enabled: false },
            },
            // Unlayer's own preview shows a fixed 1280px "desktop" inside this much narrower box, so
            // the email was cut off (and blank on phones). The page's Preview button replaces it.
            features: {
                preview: false,
            },
        };
    }

    onReady(): void {
        if (this.imageUpload) {
            this.unlayer.registerCallback('image', (file: any, done: (result: any) => void) => {
                const picked: File = file?.attachments?.[0];
                if (!picked) {
                    done({ progress: 0 });
                    return;
                }
                done({ progress: 10 });
                this.imageUpload(picked).then(
                    url => done({ progress: 100, url }),
                    error => {
                        done({ progress: 0 });
                        this._ngZone.run(() => this.imageUploadFailed.emit(error));
                    }
                );
            });
        }

        const markReady = () => {
            if (this.isReady) {
                return;
            }
            this.unlayer.addEventListener('design:updated', () => this._ngZone.run(() => this.dirty.emit()));
            this._ngZone.run(() => {
                this.isReady = true;
                this.ready.emit();
            });
        };

        if (this.design) {
            // Start listening for edits only once our own loadDesign has finished.
            this.unlayer.addEventListener('design:loaded', markReady);
            this.unlayer.loadDesign(this.design);
        } else {
            markReady();
        }
    }

    getResult(): Promise<DesignerResult> {
        if (!this.unlayer || !this.isReady) {
            return Promise.reject(new Error('The editor is still loading.'));
        }
        return new Promise(resolve => {
            this.unlayer.exportHtml((data: any) => {
                this._ngZone.run(() => resolve({ html: data.html, design: data.design }));
            });
        });
    }

    ngOnDestroy(): void {
        this.unlayer?.destroy?.();
    }
}
